import { InteractionRequiredAuthError, PublicClientApplication, type Configuration } from '@azure/msal-browser';

const TENANT_ID = __MSAL_TENANT_ID__;
const CLIENT_ID = __MSAL_CLIENT_ID__;
const REDIRECT_URI = __MSAL_REDIRECT_URI__ || window.location.origin;
/** Same backend API scope the admin app uses, e.g. "api://<backend-client-id>/access_as_admin". */
const API_SCOPE = __MSAL_API_SCOPE__;

const MISSING_CONFIG_MESSAGE =
  'Missing MSAL configuration: set ADDIN_MSAL_TENANT_ID, ADDIN_MSAL_CLIENT_ID, and ADDIN_MSAL_API_SCOPE in apps/addin/.env, then restart `npm run dev:addin`.';

if (!TENANT_ID || !CLIENT_ID || !API_SCOPE) {
  // eslint-disable-next-line no-console
  console.error(MISSING_CONFIG_MESSAGE);
}

const msalConfig: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID || 'common'}`,
    redirectUri: REDIRECT_URI,
    postLogoutRedirectUri: REDIRECT_URI,
  },
  cache: {
    cacheLocation: 'localStorage',
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);
let initialized = false;

/** MSAL requires `initialize()` to resolve before any other method is called — callers await
 * this once, up front, instead of each needing to know about it. */
export async function ensureMsalInitialized(): Promise<void> {
  if (initialized) return;
  await msalInstance.initialize();
  initialized = true;
}

export const loginRequest = {
  scopes: API_SCOPE ? [API_SCOPE] : [],
};

/** The taskpane runs inside an Office host iframe, where a full-page redirect round-trip is
 * unreliable (and in some hosts, blocked outright) — a popup keeps sign-in self-contained within
 * the pane instead of navigating it away from the add-in entirely. */
export async function signIn(): Promise<void> {
  if (!CLIENT_ID || !TENANT_ID || !API_SCOPE) throw new Error(MISSING_CONFIG_MESSAGE);
  await ensureMsalInitialized();
  const result = await msalInstance.loginPopup(loginRequest);
  msalInstance.setActiveAccount(result.account);
}

export async function signOut(): Promise<void> {
  await ensureMsalInitialized();
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  await msalInstance.logoutPopup({ account });
}

/** Silently acquires an access token for the backend API, falling back to an interactive popup
 * only when silent acquisition genuinely requires user interaction. Returns undefined (never a
 * stale/expired token) if no account is signed in yet. */
export async function getAccessToken(): Promise<string | undefined> {
  await ensureMsalInitialized();
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) return undefined;
  try {
    const result = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      try {
        const result = await msalInstance.acquireTokenPopup(loginRequest);
        return result.accessToken;
      } catch {
        return undefined;
      }
    }
    return undefined;
  }
}

export function getActiveAccount() {
  return msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
}
